import {Mapper} from '@s-ui/domain'

export class FromNewsDetailResponseToEntityMapper extends Mapper {
  constructor({config, newsDetailEntityFactory, authorEntityFactory, tagcloudSeoValueObjectFactory, tagEntityFactory}) {
    super()

    this._config = config
    this._newsDetailEntityFactory = newsDetailEntityFactory
    this._authorEntityFactory = authorEntityFactory
    this._tagcloudSeoValueObjectFactory = tagcloudSeoValueObjectFactory
    this._tagEntityFactory = tagEntityFactory
    this._categoriesPaths = config.get('editorial').categoriesUrls
    this._urlCategoryPath = config.get('page').NEWS_CATEGORY_PATH_PATTERN
    this._urlTagPath = config.get('page').NEWS_TAG_PATH_PATTERN

    const {
      NEWS_EMBEDDED_ENTRY_TYPE_IMAGE_SINGLE,
      NEWS_EMBEDDED_ENTRY_TYPE_YOUTUBE_VIDEO,
      NEWS_EMBEDDED_ENTRY_TYPE_LATEST_PLATES,
      NEWS_EMBEDDED_ENTRY_TYPE_DEFAULT,
      NEWS_YOUTUBE_PROVIDER_KEY
    } = this._config.get('COMMON')

    this.NEWS_EMBEDDED_ENTRY_TYPE_IMAGE_SINGLE = NEWS_EMBEDDED_ENTRY_TYPE_IMAGE_SINGLE
    this.NEWS_EMBEDDED_ENTRY_TYPE_YOUTUBE_VIDEO = NEWS_EMBEDDED_ENTRY_TYPE_YOUTUBE_VIDEO
    this.NEWS_EMBEDDED_ENTRY_TYPE_LATEST_PLATES = NEWS_EMBEDDED_ENTRY_TYPE_LATEST_PLATES
    this.NEWS_EMBEDDED_ENTRY_TYPE_DEFAULT = NEWS_EMBEDDED_ENTRY_TYPE_DEFAULT
    this.NEWS_YOUTUBE_PROVIDER_KEY = NEWS_YOUTUBE_PROVIDER_KEY
  }

  _getLocalizedValue = value => value?.[this._locale]

  _getImages = ({imagesGalleries}) => {
    if (!imagesGalleries) return []

    const [gallery] = this._getLocalizedValue(imagesGalleries)
    const {fields: {photoGallery} = {}} = gallery || {}

    return (photoGallery && photoGallery[this._locale]) || []
  }

  _getAuthor = ({author}) => {
    if (!author) return {}
    const {fields: {name, photo, slug} = {}} = this._getLocalizedValue(author)

    const {fields: {file} = {}} = (photo && this._getLocalizedValue(photo)) || {}

    const {details, url: imageUrl} = (file && this._getLocalizedValue(file)) || {}

    const {image: imageSize} = details

    return this._authorEntityFactory({
      label: name && this._getLocalizedValue(name),
      image: imageUrl,
      imageSize,
      slug: slug && this._getLocalizedValue(slug)
    })
  }

  _getValidCategories = ({metadata}) => {
    const {tags} = metadata
    const EXCLUDED_TAG_IDS = [
      'coches',
      'motos',
      'homeColumn1',
      'homeColumn2',
      'homeColumn3',
      'homeHighlightedGroup1',
      'homeHighlightedGroup2',
      'homeHighlightedGroup3',
      'wdyl',
      'boletin'
    ]

    return tags.filter(tag => !EXCLUDED_TAG_IDS.includes(tag.sys.id))
  }

  _getCategory = ({metadata}) => {
    const DEFAULT_TAG = ''
    const [category] = this._getValidCategories({metadata})

    return category?.sys?.id || DEFAULT_TAG
  }

  _generateTag = ({category}) => {
    const url = this._categoriesPaths.includes(category)
      ? this._urlCategoryPath.replace(':category', category)
      : this._urlTagPath.replace(':tag', category)

    return this._tagEntityFactory({
      id: category,
      url,
      text: category?.toUpperCase(),
      rel: 'tag'
    })
  }

  _getVehicles = ({vehicleRelationshipsJson}) => {
    if (!vehicleRelationshipsJson) return []

    return this._getLocalizedValue(vehicleRelationshipsJson).map(({make, makeId, model, modelId, year}) => ({
      make: {id: makeId, name: make},
      model: modelId ? {id: modelId, name: model} : null,
      year
    }))
  }

  _getEmbeddedCustomModuleType = ({fields}) => {
    const {CUSTOM_MODULE_TYPE_LATEST_PLATES, NEWS_CONTENT_TYPE_CUSTOM_MODULE} = this._config.get('COMMON')

    switch (fields.attributes[this._locale]?.[NEWS_CONTENT_TYPE_CUSTOM_MODULE.TYPE]) {
      case CUSTOM_MODULE_TYPE_LATEST_PLATES:
        return this.NEWS_EMBEDDED_ENTRY_TYPE_LATEST_PLATES

      default:
        return this.NEWS_EMBEDDED_ENTRY_TYPE_DEFAULT
    }
  }

  _getEmbeddedVideoType = ({fields}) => {
    switch (fields.provider[this._locale]) {
      case this.NEWS_YOUTUBE_PROVIDER_KEY:
        return this.NEWS_EMBEDDED_ENTRY_TYPE_YOUTUBE_VIDEO

      default:
        return this.NEWS_EMBEDDED_ENTRY_TYPE_DEFAULT
    }
  }

  _getEmbeddedEntryType = entry => {
    const {
      fields,
      contentType: {name}
    } = entry
    const {NEWS_CONTENT_TYPES} = this._config.get('COMMON')

    if (name === NEWS_CONTENT_TYPES.CUSTOM_MODULE) return this._getEmbeddedCustomModuleType({fields})
    if (name === NEWS_CONTENT_TYPES.IMAGE) return this.NEWS_EMBEDDED_ENTRY_TYPE_IMAGE_SINGLE
    if (name === NEWS_CONTENT_TYPES.VIDEO) return this._getEmbeddedVideoType({fields})

    return this.NEWS_EMBEDDED_ENTRY_TYPE_DEFAULT
  }

  _getEmbeddedSingleImageEntryInfo = ({footer, imageUrlEnhanced}) => {
    if (imageUrlEnhanced && this._getLocalizedValue(imageUrlEnhanced)) {
      const {imageUrl} = this._getLocalizedValue(imageUrlEnhanced)
      const imageCaption = this._getLocalizedValue(footer)

      return {
        imageUrl,
        imageCaption
      }
    } else {
      const FALLBACK_IMAGE_FOOTER = ''
      const urlStatics = this._config.get('urlStatics')
      const DEFAULT_PHOTO = this._config.get('DEFAULT_IMAGE_PLACEHOLDER')
      const fallbackImageUrl = `${urlStatics}${DEFAULT_PHOTO}`

      return {
        imageUrl: fallbackImageUrl,
        imageCaption: FALLBACK_IMAGE_FOOTER
      }
    }
  }

  _getEmbeddedYoutubeVideoEntryInfo = ({id, title}) => {
    const videoId = this._getLocalizedValue(id)
    const videoTitle = this._getLocalizedValue(title)

    const getYoutubeNoCookieUrl = id => `https://www.youtube-nocookie.com/embed/${id}?rel=0`
    const getYoutubeThumbnailMaxRes = id => `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`
    const getYoutubeThumbnailHQ = id => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`

    return {
      videoId,
      videoURL: getYoutubeNoCookieUrl(videoId),
      thumbnailMaxRes: getYoutubeThumbnailMaxRes(videoId),
      thumbnailHQ: getYoutubeThumbnailHQ(videoId),
      title: videoTitle
    }
  }

  _getEmbeddedLatestPlateEntryInfo = () => {
    return {
      latestVehiclePlateEnabled: true
    }
  }

  _getEmbeddedEntryInfo = (entryType, fields) => {
    const entryTypeMap = {
      [this.NEWS_EMBEDDED_ENTRY_TYPE_IMAGE_SINGLE]: this._getEmbeddedSingleImageEntryInfo,
      [this.NEWS_EMBEDDED_ENTRY_TYPE_YOUTUBE_VIDEO]: this._getEmbeddedYoutubeVideoEntryInfo,
      [this.NEWS_EMBEDDED_ENTRY_TYPE_LATEST_PLATES]: this._getEmbeddedLatestPlateEntryInfo,
      [this.NEWS_EMBEDDED_ENTRY_TYPE_DEFAULT]: () => null
    }

    return entryTypeMap[entryType](fields)
  }

  /*
    An embedded entry is a custom entry inserted in the middle of the article body (rich text).
    It can be a custom entry or a native Contentful entry.
    Contentful's library is able to read plain text (paragraph, strongs, links, etc) but not this kind of content.
    Examples: image with text, videos, multiple images.
  */
  _getCustomContentConfig = ({body}) => {
    const customContentCollection = {}
    const typeCounters = {}

    body.content.forEach(({data}) => {
      if (data) {
        const entryId = data.sys.id
        const entryType = this._getEmbeddedEntryType(data)
        const entryInfo = this._getEmbeddedEntryInfo(entryType, data.fields)

        if (!typeCounters[entryType]) typeCounters[entryType] = 0
        const index = typeCounters[entryType]++

        customContentCollection[entryId] = {entryType, entryInfo: {...entryInfo, index}}
      }
    })

    return customContentCollection
  }

  _getSeoTagsForTagclouds = ({seoTags}) => {
    const hasSeoTags = Boolean(seoTags && this._getLocalizedValue(seoTags).length)

    return hasSeoTags
      ? this._getLocalizedValue(seoTags).map(tag => ({
          link: tag.fields.slug[this._locale],
          label: tag.fields.text[this._locale]
        }))
      : []
  }

  _getCategoriesForTagclouds = ({metadata}) => {
    const {NEWS_TAG_PATH_PATTERN} = this._config.get('page')
    const {CATEGORIES_LISTING_URLS} = this._config.get('editorial')
    const validCategories = this._getValidCategories({metadata})

    return validCategories.map(extraCategory => {
      const link =
        CATEGORIES_LISTING_URLS[extraCategory.sys.id] || NEWS_TAG_PATH_PATTERN.replace(':tag', extraCategory.sys.id)

      return {
        label: extraCategory.sys.id,
        link
      }
    })
  }

  _getTagclouds = ({seoTags, metadata}) => {
    const tags = this._getSeoTagsForTagclouds({seoTags})
    const relatedCategories = this._getCategoriesForTagclouds({metadata})

    return [
      this._tagcloudSeoValueObjectFactory({
        title: 'TAGCLOUD_NEWS_DETAIL',
        tags: [...relatedCategories, ...tags]
      }).toJSON()
    ]
  }

  _getSeoMetaInformation = ({seoDescription, seoTitle}) => ({
    title: this._getLocalizedValue(seoTitle),
    description: this._getLocalizedValue(seoDescription)
  })

  _getProsAndCons = ({expertOpinion}) => {
    if (!expertOpinion) return []

    return expertOpinion[this._locale].map(experOpinion => {
      const {
        fields: {like, unlike, title}
      } = experOpinion

      return {
        pros: this._getLocalizedValue(like),
        cons: this._getLocalizedValue(unlike),
        vehicleName: this._getLocalizedValue(title)
      }
    })
  }

  _getRelatedContent = ({relatedContent}) => {
    const MAX_RELATED_NEWS_COUNT = 2
    const DEFAULT_RELATED_CONTENT = []

    if (!relatedContent || !this._getLocalizedValue(relatedContent).length) return DEFAULT_RELATED_CONTENT

    return this._getLocalizedValue(relatedContent)
      .slice(0, MAX_RELATED_NEWS_COUNT)
      .map(({fields, metadata, sys}) => {
        const {imagesGalleries, slug, subtitle, title} = fields

        const payload = {
          fields: {imagesGalleries, slug, subtitle, title},
          metadata,
          sys
        }

        return this._mapPayload({payload})
      })
  }

  // TODO: consider to move it to a smaller mapper
  // https://jira.ets.mpi-internal.com/browse/MTR-67872
  _getValoration = ({valoration}) => {
    if (!valoration) return []

    const GLOBAL_SCORE_KEY = 'globalQualification'
    const VEHICLE_NAME_KEY = 'title'
    const VEHICLE_REFERENCE_KEY = 'relatedVehicleId'

    const SCORES_LIST_EXLUSION = [GLOBAL_SCORE_KEY, VEHICLE_NAME_KEY, VEHICLE_REFERENCE_KEY]
    const VALORATIONS_LITERALS_MAP = {
      behaviour: 'NEWS.DETAIL_VALORATION_LABEL_BEHAVIOUR',
      comfort: 'NEWS.DETAIL_VALORATION_LABEL_COMFORT',
      consumptionAutonomy: 'NEWS.DETAIL_VALORATION_LABEL_CONSUMPTION_OR_AUTONOMY',
      cycle: 'NEWS.DETAIL_VALORATION_LABEL_CYCLE',
      engine: 'NEWS.DETAIL_VALORATION_LABEL_ENGINE',
      equipment: 'NEWS.DETAIL_VALORATION_LABEL_EQUIPMENT',
      ergonomics: 'NEWS.DETAIL_VALORATION_LABEL_ERGONOMICS',
      habitability: 'NEWS.DETAIL_VALORATION_LABEL_HABITABILITY',
      priceQualityRatio: 'NEWS.DETAIL_VALORATION_LABEL_VALUE_FOR_MONEY',
      security: 'NEWS.DETAIL_VALORATION_LABEL_SECURITY'
    }

    const vehiclesValorations = this._getLocalizedValue(valoration)

    return vehiclesValorations.map(vehicleValoration => {
      return {
        scoresList: Object.keys(vehicleValoration.fields)
          .filter(fieldName => !SCORES_LIST_EXLUSION.includes(fieldName))
          .map(scoreKey => {
            return {
              id: scoreKey,
              labelLiteral: VALORATIONS_LITERALS_MAP[scoreKey],
              value: this._getLocalizedValue(vehicleValoration.fields[scoreKey])
            }
          }),

        globalScore: {
          labelLiteral: 'NEWS.DETAIL_VALORATION_LABEL_GLOBAL_SCORE',
          value: this._getLocalizedValue(vehicleValoration.fields[GLOBAL_SCORE_KEY])
        },

        vehicle: {
          name: this._getLocalizedValue(vehicleValoration.fields[VEHICLE_NAME_KEY]),
          referenceId: this._getLocalizedValue(vehicleValoration.fields[VEHICLE_REFERENCE_KEY])
        }
      }
    })
  }

  _getVideo = ({customContentConfig}) => {
    if (!customContentConfig) return null

    const videoEntry = Object.values(customContentConfig).find(
      ({entryType}) => entryType === this.NEWS_EMBEDDED_ENTRY_TYPE_YOUTUBE_VIDEO
    )

    if (!videoEntry) return null

    const {videoId, thumbnailMaxRes, thumbnailHQ, title} = videoEntry.entryInfo
    const embedUrl = `https://www.youtube.com/embed/${videoId}`

    return {videoId, embedUrl, thumbnailMaxRes, thumbnailHQ, title}
  }

  _getTotalNumberOfComments = ({numComments = 0}) => numComments

  _mapPayload = ({payload, numComments}) => {
    const {fields, metadata, rawFields = {}, sys = {}} = payload

    const {
      allowComments,
      author,
      body: payloadBody,
      expertOpinion,
      imagesGalleries,
      relatedContent,
      seoDescription,
      seoTags,
      seoTitle,
      slug,
      subtitle,
      title,
      valoration,
      vehicleRelationshipsJson,
      visualDate
    } = fields

    const {body} = rawFields

    const {createdAt, id, updatedAt} = sys

    const category = this._getCategory({metadata})

    const customContentConfig = payloadBody
      ? this._getCustomContentConfig({body: this._getLocalizedValue(payloadBody)})
      : null

    return {
      allowComments: Boolean(this._getLocalizedValue(allowComments)),
      author: this._getAuthor({author}),
      body: this._getLocalizedValue(body),
      category,
      tag: this._generateTag({category}),
      ...(customContentConfig && {customContentConfig}),
      date: this._getLocalizedValue(visualDate),
      description: this._getLocalizedValue(subtitle),
      id,
      images: this._getImages({imagesGalleries}),
      link: this._getLocalizedValue(slug),
      prosAndCons: this._getProsAndCons({expertOpinion}),
      publicationDate: createdAt,
      relatedContent: this._getRelatedContent({relatedContent}),
      seoMetaInformation: this._getSeoMetaInformation({
        seoDescription,
        seoTitle
      }),
      tagclouds: this._getTagclouds({seoTags, metadata}),
      title: this._getLocalizedValue(title),
      totalNumberOfComments: this._getTotalNumberOfComments({numComments}),
      updateDate: updatedAt,
      valoration: this._getValoration({valoration}),
      vehicles: this._getVehicles({vehicleRelationshipsJson}),
      video: this._getVideo({customContentConfig})
    }
  }

  setParams = ({locale}) => {
    this._locale = locale
    return this
  }

  map = ({newsDetailResponse}) => {
    const {payload, numComments} = newsDetailResponse
    const mappedPayload = this._mapPayload({payload, numComments})

    return this._newsDetailEntityFactory({...mappedPayload})
  }
}
